import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { SearchResult, NodeType } from '@/types'

export function useSearch() {
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<Error | null>(null)

  const search = useCallback(async (
    query: string,
    options: {
      limit?: number
      threshold?: number
      nodeTypes?: NodeType[]
      domainId?: string
      minLevel?: number
      maxLevel?: number
    } = {}
  ) => {
    if (!query.trim()) {
      setResults([])
      return
    }

    setIsSearching(true)
    setSearchError(null)

    try {
      const {
        limit = 20,
        threshold = 0.5,
        nodeTypes,
        domainId,
        minLevel,
        maxLevel
      } = options

      // Use Supabase's semantic search function
      const { data: semanticResults, error: semanticError } = await supabase
        .rpc('search_similar', {
          query_text: query,
          match_count: limit,
          match_threshold: threshold
        })

      if (semanticError) {
        // Fallback to regular text search if semantic search fails
        // silent fallback to text search
        
        let conceptQuery = supabase
          .from('concepts')
          .select(`
            id,
            name,
            definition,
            domain_id,
            complexity_level,
            synapse_strength
          `)
          .or(`name.ilike.%${query.replace(/[%_(),]/g, '').replace(/'/g, "''")}%,definition.ilike.%${query.replace(/[%_(),]/g, '').replace(/'/g, "''")}%`)

        // Apply filters
        if (domainId) conceptQuery = conceptQuery.eq('domain_id', domainId)
        if (minLevel) conceptQuery = conceptQuery.gte('complexity_level', minLevel)
        if (maxLevel) conceptQuery = conceptQuery.lte('complexity_level', maxLevel)

        const { data: concepts, error: conceptError } = await conceptQuery
          .order('synapse_strength', { ascending: false })
          .limit(limit)

        if (conceptError) throw conceptError

        const conceptResults: SearchResult[] = concepts?.map(concept => ({
          node_id: concept.id,
          node_type: 'concept' as NodeType,
          content: `${concept.name}: ${concept.definition}`,
          similarity: 0.8 // Default similarity for text search
        })) || []

        setResults(conceptResults)
        return conceptResults
      }

      // Process semantic search results
      let searchResults = semanticResults?.map((result: {
        id: string
        node_type: string
        content: string
        similarity: number
      }) => ({
        node_id: result.id,
        node_type: result.node_type || 'concept',
        content: result.content,
        similarity: result.similarity
      })) || []

      // Filter by node types if specified
      if (nodeTypes && nodeTypes.length > 0) {
        searchResults = searchResults.filter((result: SearchResult) =>
          nodeTypes.includes(result.node_type)
        )
      }

      setResults(searchResults)
      return searchResults

    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error))
      setSearchError(errorObj)
      setResults([])
      return []
    } finally {
      setIsSearching(false)
    }
  }, [])

  const clearResults = useCallback(() => {
    setResults([])
    setSearchError(null)
  }, [])

  return {
    results,
    isSearching,
    searchError,
    search,
    clearResults
  }
}

// Hook for searching within a specific context
export function useContextualSearch(contextNodeId?: string) {
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<Error | null>(null)

  const search = useCallback(async (query: string, limit = 10) => {
    if (!query.trim() || !contextNodeId) {
      setResults([])
      return
    }

    setIsSearching(true)
    setSearchError(null)

    try {
      // First, get connected nodes to the context
      const { data: connections } = await supabase
        .from('connections')
        .select('from_node_id, to_node_id, from_node_type, to_node_type')
        .or(`from_node_id.eq.${contextNodeId},to_node_id.eq.${contextNodeId}`)

      // Get related node IDs
      const relatedIds = new Set<string>()
      connections?.forEach(conn => {
        if (conn.from_node_id !== contextNodeId) relatedIds.add(conn.from_node_id)
        if (conn.to_node_id !== contextNodeId) relatedIds.add(conn.to_node_id)
      })

      // Search within related concepts
      if (relatedIds.size > 0) {
        const { data: concepts } = await supabase
          .from('concepts')
          .select('id, name, definition')
          .in('id', Array.from(relatedIds))
          .or(`name.ilike.%${query.replace(/[%_(),]/g, '').replace(/'/g, "''")}%,definition.ilike.%${query.replace(/[%_(),]/g, '').replace(/'/g, "''")}%`)
          .limit(limit)

        const contextResults: SearchResult[] = concepts?.map(concept => ({
          node_id: concept.id,
          node_type: 'concept' as NodeType,
          content: `${concept.name}: ${concept.definition}`,
          similarity: 0.9 // Higher similarity for contextual matches
        })) || []

        setResults(contextResults)
        return contextResults
      }

      setResults([])
      return []

    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error))
      setSearchError(errorObj)
      setResults([])
      return []
    } finally {
      setIsSearching(false)
    }
  }, [contextNodeId])

  return {
    results,
    isSearching,
    searchError,
    search,
    clearResults: () => setResults([])
  }
}

// Hook for search suggestions
export function useSearchSuggestions() {
  const [suggestions, setSuggestions] = useState<string[]>([])

  const getSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([])
      return
    }

    try {
      // Get concept names that start with the query
      const { data } = await supabase
        .from('concepts')
        .select('name')
        .ilike('name', `${query}%`)
        .order('synapse_strength', { ascending: false })
        .limit(5)

      const conceptSuggestions = data?.map(c => c.name) || []
      setSuggestions(conceptSuggestions)
    } catch {
      // silent fallback
      setSuggestions([])
    }
  }, [])

  return {
    suggestions,
    getSuggestions,
    clearSuggestions: () => setSuggestions([])
  }
}